<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg p-6 text-white">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold">Planes de Suscripción</h1>
          <p class="text-yellow-100 mt-1">Gestiona los planes Free y Premium</p>
        </div>
        <div class="hidden sm:block">
          <div class="bg-white/20 rounded-lg p-4">
            <svg class="h-12 w-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions bar -->
    <div class="card p-5">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p class="text-sm text-gray-500">{{ plans.length }} plan{{ plans.length !== 1 ? 'es' : '' }} registrado{{ plans.length !== 1 ? 's' : '' }}</p>
        </div>
        <button
          @click="$router.push('/subscriptions/create')"
          class="inline-flex items-center px-4 py-2.5 text-sm font-semibold rounded-lg shadow-md transition-opacity hover:opacity-90"
          style="background-color:#1d4ed8;color:#fff"
        >
          <svg class="-ml-0.5 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Nuevo Plan
        </button>
      </div>
    </div>

    <!-- Table -->
    <div class="card">
      <DataTable
        :value="plans"
        :loading="loading"
        tableStyle="min-width: 50rem"
        class="p-datatable-sm"
      >
        <template #header>
          <div class="flex justify-between items-center py-1">
            <h2 class="text-lg font-semibold text-gray-900">Lista de Planes</h2>
          </div>
        </template>

        <template #empty>
          <div class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No hay planes</h3>
            <p class="mt-1 text-sm text-gray-500">Crea el primer plan de suscripción.</p>
          </div>
        </template>

        <template #loading>
          <div class="text-center py-8">
            <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
            <p class="mt-2 text-gray-600">Cargando planes...</p>
          </div>
        </template>

        <Column field="name" header="Plan" sortable>
          <template #body="{ data }">
            <div class="flex items-center gap-3">
              <div
                class="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"
                :style="data.type === 'premium' ? 'background:linear-gradient(135deg,#f59e0b,#d97706)' : 'background:linear-gradient(135deg,#6b7280,#4b5563)'"
              >
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p class="text-sm font-semibold text-gray-900">{{ data.name }}</p>
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold mt-0.5"
                  :style="data.type === 'premium' ? 'background:#fef3c7;color:#92400e' : 'background:#f3f4f6;color:#374151'"
                >{{ data.type === 'premium' ? 'Premium' : 'Free' }}</span>
              </div>
            </div>
          </template>
        </Column>

        <Column field="price" header="Precio" sortable>
          <template #body="{ data }">
            <span class="text-sm font-semibold text-gray-900">
              {{ data.price === 0 ? 'Gratis' : `$${Number(data.price).toFixed(2)}` }}
            </span>
          </template>
        </Column>

        <Column field="durationDays" header="Duración" sortable>
          <template #body="{ data }">
            <span class="text-sm text-gray-700">
              {{ data.durationDays === 0 ? 'Indefinida' : `${data.durationDays} días` }}
            </span>
          </template>
        </Column>

        <Column field="maxPets" header="Mascotas" sortable>
          <template #body="{ data }">
            <span class="text-sm font-medium text-gray-900">{{ data.maxPets }}</span>
          </template>
        </Column>

        <Column header="Likes / Super-likes">
          <template #body="{ data }">
            <div class="flex items-center gap-2">
              <span class="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100">
                {{ data.likesPerDay === -1 ? '∞' : data.likesPerDay }} likes
              </span>
              <span class="inline-flex items-center px-2 py-0.5 rounded bg-purple-50 text-purple-700 text-xs font-medium border border-purple-100">
                {{ data.superLikesPerDay }} super
              </span>
            </div>
          </template>
        </Column>

        <Column header="Usuarios">
          <template #body="{ data }">
            <span class="text-sm text-gray-700">{{ data._count?.subscriptions ?? 0 }}</span>
          </template>
        </Column>

        <Column field="isActive" header="Estado">
          <template #body="{ data }">
            <span
              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
              :style="data.isActive ? 'background:#dcfce7;color:#166534' : 'background:#fee2e2;color:#991b1b'"
            >
              {{ data.isActive ? 'Activo' : 'Inactivo' }}
            </span>
          </template>
        </Column>

        <Column header="Acciones">
          <template #body="{ data }">
            <div class="flex items-center gap-2">
              <button
                @click="$router.push(`/subscriptions/${data.id}/edit`)"
                style="background-color:#1d4ed8;color:#fff"
                class="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-lg transition-opacity hover:opacity-90"
              >
                <svg class="h-3.5 w-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Editar
              </button>
              <button
                @click="togglePlan(data)"
                style="background-color:#475569;color:#fff"
                class="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-lg transition-opacity hover:opacity-90"
              >
                {{ data.isActive ? 'Desactivar' : 'Activar' }}
              </button>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { subscriptionService } from '../services/subscriptionService'

const toast = useToast()
const plans = ref<any[]>([])
const loading = ref(false)

async function loadPlans() {
  loading.value = true
  try {
    const res = await subscriptionService.getPlans()
    plans.value = res.data
  } catch {
    toast.error('Error al cargar los planes')
  } finally {
    loading.value = false
  }
}

async function togglePlan(plan: any) {
  try {
    await subscriptionService.togglePlan(plan.id, !plan.isActive)
    await loadPlans()
    toast.success(`Plan ${plan.isActive ? 'desactivado' : 'activado'}`)
  } catch {
    toast.error('Error al cambiar el estado del plan')
  }
}

onMounted(loadPlans)
</script>
